import { FC, ReactElement, useMemo } from 'react';

import { cva } from 'class-variance-authority';
import { cn } from 'utils/helpers';
import { TbDotsVertical } from 'react-icons/tb';
import { SIZE } from 'utils/constants';

import NavAction from '../NavAction';

interface TabItem {
  value: string;
  path?: string;
  label: string;
  iconLeft?: ReactElement;
  iconRight?: ReactElement;
  disabled?: boolean;
}

interface PROPS {
  items: TabItem[];
  type?: 'primary' | 'secondary' | 'tertiary';
  size?: SIZE;
  selectedTab: string;
  className?: string | string[];
  onChange: (selectedTab: TabItem) => void;
  isLoading?: boolean;
  maxItems?: number;
}

const tabClasses = cva(
  [
    'box-border',
    'text-content',
    'hover:text-primary-base',
    'cursor-pointer',
    'transition duration-300',
    'flex items-center gap-x-2.5',
  ],
  {
    variants: {
      type: {
        primary: [
          'bg-fill-tertiary',
          'border-b-2 border-transparent rounded-t-[6px]',
          'capitalize',
          'shadow-sm',
        ],
        secondary: [
          'bg-fill-tertiary',
          'border-b-2 border-transparent',
          'capitalize',
        ],
        tertiary: ['bg-transparent', 'rounded', 'uppercase !leading-none'],
      },
      disabled: {
        true: ['text-disabled', 'pointer-events-none'],
        false: [],
      },
    },
    defaultVariants: { type: 'primary' },
  },
);

const selectedClasses = cva(['z-10 font-semibold'], {
  variants: {
    type: {
      primary: ['border-primary-base', 'text-secondary-dark'],
      secondary: ['border-primary-base', 'text-secondary-dark'],
      tertiary: ['bg-fill-primary', 'text-secondary-base font-semibold'],
    },
  },
  defaultVariants: { type: 'primary' },
});

const Tabs: FC<PROPS> = ({
  items,
  selectedTab,
  onChange,
  type,
  size,
  isLoading,
  className,
  maxItems,
}) => {
  const displayedTabs = useMemo(
    () => items.filter((_, index) => index < (maxItems ?? items.length)),
    [items, maxItems],
  );

  const hiddenItems = useMemo(
    () => items.filter((_, index) => index >= (maxItems ?? items.length)),
    [items, maxItems],
  );

  return (
    <div className={cn('flex items-center gap-x-3', className)}>
      {displayedTabs.map(({ value, label, iconLeft, iconRight, disabled }) => (
        <div
          key={value}
          className={cn(
            tabClasses({ type, disabled: disabled || isLoading }),
            selectedTab === value && selectedClasses({ type }),
            size === SIZE.Small &&
              type === 'primary' &&
              'py-2 px-[18px] text-sm leading-[20px]',
            size === SIZE.Medium &&
              type === 'primary' &&
              'py-3 px-5 text-[16px] leading-[22px]',
            size === SIZE.Large &&
              type === 'primary' &&
              'py-[18px] px-6 text-[18px] leading-[20px]',
            size === SIZE.Small &&
              type === 'secondary' &&
              'py-[14px] px-[18px] text-sm',
            size === SIZE.Medium &&
              type === 'secondary' &&
              'py-4 px-5 text-[16px] leading-[22px]',
            size === SIZE.Large &&
              type === 'secondary' &&
              'py-[18px] px-6 text-[18px] leading-[20px]',
            size === SIZE.Small &&
              type === 'tertiary' &&
              'py-3 px-[18px] text-xs',
            size === SIZE.Medium && type === 'tertiary' && 'py-3 px-5 text-sm',
            size === SIZE.Large && type === 'tertiary' && 'py-3 px-6 text-base',
          )}
          onClick={() => onChange({ value, label })}
        >
          {iconLeft}
          {label}
          {iconRight}
        </div>
      ))}
      {!!hiddenItems.length && (
        <NavAction
          label={<TbDotsVertical className="mr-0 h-5 w-5 text-content" />}
          className={cn(
            'border border-separator-primary',
            'rounded-[6px] px-2 py-[6px]',
            'flex items-center',
          )}
          options={hiddenItems.map(({ label, value }) => ({
            text: label,
            key: value,
            isSelected: selectedTab === value,
            onClick: () => onChange({ value, label }),
          }))}
          optionsClassName="w-[190px] right-0 top-8 space-y-[14px]"
        />
      )}
    </div>
  );
};

export default Tabs;
